import { memo } from "react";
import { Link, generatePath } from "react-router";

import { isDev } from "@/lib/chillnn/enviroment";
import type { SideMenuPcQuery } from "@/lib/gql/__generated__/graphql";

import { externalLink } from "@/routes/_constants";
import { PATHS } from "@/routes/paths";

import * as Icon from "@/components/features/side-menu/_shared/icon";

import OpenInNew from "@/components/ui/icon/open-in-new";
import { Image } from "@/components/ui/image/image";
import { Label } from "@/components/ui/label/label";

import { SideMenuItemOther } from "./components";
import { Trial } from "../_shared/components";
import { SideMenuItem } from "../_shared/components/side-menu-item";
import { SideMenuItemWrapper } from "../_shared/components/side-menu-item-wrapper";

type Props = {
	hotelID: string;
	sideMenuData: SideMenuPcQuery;
	hotelApplicationData: {
		hotelApplicationStatus: {
			isHotelPolicyRegistered: boolean;
			isHotelRegistered: boolean;
			validDtEndBasicInitialSetUp: string | null;
		};
	};
};

export const SideMenuPCPresenter = memo(
	({
		hotelID,
		sideMenuData,
		hotelApplicationData: {
			hotelApplicationStatus: { isHotelPolicyRegistered, isHotelRegistered, validDtEndBasicInitialSetUp },
		},
	}: Props) => {
		// 新規予約リクエストの数
		const newRequestReservationCount = sideMenuData.reservationRequestListByMonthCount.reservations.length;
		// HPBの項目を表示するかどうか
		const isShowHPB = !!sideMenuData.hpbSettings;

		return (
			<aside className="relative h-screen max-h-screen w-full overflow-y-scroll overscroll-y-contain border-r border-zinc-100 py-8 pl-5 pr-6 hidden-scroll-bar">
				<div className="flex flex-col gap-y-4">
					<div className="flex items-center justify-between">
						<Link to={"/"} className="block w-1/2 duration-500 ease-out hover:scale-[1.03]" viewTransition>
							<Image src="/chillnn-logo-black.png" className="ml-2.5" width={70} height={45} />
						</Link>

						{isDev && (
							<Label className="cursor-help select-none" variant="pink" size="lg" text="開発環境" />
						)}
					</div>

					{!!sideMenuData.hotel.name && (
						<div className="m-2 space-y-4 rounded border border-zinc-100 p-3">
							<p className="line-clamp-5 break-all text-sm">{sideMenuData.hotel.name}</p>

							<Trial validDtEndBasicInitialSetUp={validDtEndBasicInitialSetUp} />
						</div>
					)}

					<div className="flex h-full flex-col content-between">
						<div className="mb-[72px] flex flex-col">
							<SideMenuItem
								href={generatePath(PATHS.HOTEL.TOP, { hotelID })}
								title={"ホーム"}
								icon={<Icon.HomeIcon />}
							/>

							{isHotelPolicyRegistered && (
								<SideMenuItemWrapper
									title={"インボイス制度"}
									childrenPaths={[generatePath(PATHS.HOTEL.INVOICE_SYSTEM.SETTINGS, { hotelID })]}
									icon={<Icon.InvoiceSystemIcon />}
								>
									<SideMenuItem
										href={generatePath(PATHS.HOTEL.INVOICE_SYSTEM.SETTINGS, { hotelID })}
										title="設定"
									/>
								</SideMenuItemWrapper>
							)}

							{isShowHPB && isHotelPolicyRegistered && (
								<SideMenuItem
									href={generatePath(PATHS.HOTEL.HPB.LIST, { hotelID })}
									title={"HP作成"}
									icon={<Icon.HomePageBuilderIcon />}
								/>
							)}

							{isHotelPolicyRegistered && (
								<SideMenuItemWrapper
									title={"デザイン"}
									useAmbiguousMatching
									childrenPaths={[
										generatePath(PATHS.HOTEL.RESERVATION_PAGE.BASE, { hotelID }),
										generatePath(PATHS.HOTEL.RESERVATION_PAGE.MAGAZINE.LIST.PLAN, { hotelID }),
										generatePath(PATHS.HOTEL.RESERVATION_PAGE.MAGAZINE.LIST.HOTEL, { hotelID }),
										generatePath(PATHS.HOTEL.RESERVATION_PAGE.MAGAZINE.LIST.ROOM, { hotelID }),
										generatePath(PATHS.HOTEL.RESERVATION_PAGE.MAGAZINE.SETTING, { hotelID }),
										generatePath(PATHS.HOTEL.RESERVATION_PAGE.SNACK.LIST, { hotelID }),
									]}
									icon={<Icon.DesignIcon />}
								>
									<SideMenuItem
										href={generatePath(PATHS.HOTEL.RESERVATION_PAGE.BASE, { hotelID })}
										title="プレーン"
									/>
									<SideMenuItem
										href={generatePath(PATHS.HOTEL.RESERVATION_PAGE.MAGAZINE.LIST.HOTEL, {
											hotelID,
										})}
										ambiguousPath={[`/${hotelID}/reservationPage/magazine`]}
										title={"マガジン"}
									/>
									<SideMenuItem
										href={generatePath(PATHS.HOTEL.RESERVATION_PAGE.SNACK.LIST, { hotelID })}
										ambiguousPath={[
											generatePath(PATHS.HOTEL.RESERVATION_PAGE.SNACK.LIST, { hotelID }),
										]}
										title={"スナック"}
									/>

									<div className="my-3 h-px w-full border-b border-zinc-200" />

									<SideMenuItem
										targetBlank
										href="https://chillnn.notion.site/25cfffe15c57802b8293f5e4de6921f2"
										title={
											<div className="flex items-center gap-x-2">
												<p>３つのデザインの違い</p>

												<OpenInNew className="size-[18px]" />
											</div>
										}
									/>
								</SideMenuItemWrapper>
							)}

							{isHotelPolicyRegistered && (
								<SideMenuItemWrapper
									title={"マーケティング"}
									useAmbiguousMatching
									childrenPaths={[
										generatePath(PATHS.HOTEL.MARKETING.CAMPAIGN.TOP, { hotelID }),
										generatePath(PATHS.HOTEL.MARKETING.VOUCHER.TOP, { hotelID }),
									]}
									icon={<Icon.MarketingIcon />}
								>
									<SideMenuItem
										href={generatePath(PATHS.HOTEL.MARKETING.CAMPAIGN.TOP, { hotelID })}
										ambiguousPath={[generatePath(PATHS.HOTEL.MARKETING.CAMPAIGN.TOP, { hotelID })]}
										title={"キャンペーン"}
									/>
									<SideMenuItem
										href={generatePath(PATHS.HOTEL.MARKETING.VOUCHER.TOP, { hotelID })}
										ambiguousPath={[generatePath(PATHS.HOTEL.MARKETING.VOUCHER.TOP, { hotelID })]}
										title={"宿泊券"}
									/>
								</SideMenuItemWrapper>
							)}

							{isHotelPolicyRegistered && (
								<SideMenuItemWrapper
									title={"顧客管理"}
									useAmbiguousMatching
									childrenPaths={[
										generatePath(PATHS.HOTEL.MANAGEMENT.RESERVATION.TOP, { hotelID }), //
										generatePath(PATHS.HOTEL.MANAGEMENT.SALES.TOP, { hotelID }),
										generatePath(PATHS.HOTEL.MANAGEMENT.REFUND_REQUEST.TOP, { hotelID }),
										generatePath(PATHS.HOTEL.MANAGEMENT.EC_SALES.TOP, { hotelID }),
										generatePath(PATHS.HOTEL.MANAGEMENT.ACTIVITIES.OPTIONS, { hotelID }),
									]}
									icon={<Icon.CustomerIcon />}
								>
									<SideMenuItem
										href={generatePath(PATHS.HOTEL.MANAGEMENT.RESERVATION.TOP, { hotelID })}
										ambiguousPath={[
											generatePath(PATHS.HOTEL.MANAGEMENT.RESERVATION.TOP, { hotelID }),
										]}
										title="予約"
										titleRightChildren={
											newRequestReservationCount > 0 ? (
												<Label text={newRequestReservationCount.toString()} variant="circle" />
											) : undefined
										}
									/>
									<SideMenuItem
										href={generatePath(PATHS.HOTEL.MANAGEMENT.EC_SALES.TOP, { hotelID })}
										ambiguousPath={[generatePath(PATHS.HOTEL.MANAGEMENT.EC_SALES.TOP, { hotelID })]}
										title={"EC販売"}
									/>
									<SideMenuItem
										href={generatePath(PATHS.HOTEL.MANAGEMENT.SALES.TOP, { hotelID })}
										ambiguousPath={[generatePath(PATHS.HOTEL.MANAGEMENT.SALES.TOP, { hotelID })]}
										title={"収支"}
									/>
									<SideMenuItem
										href={generatePath(PATHS.HOTEL.MANAGEMENT.REFUND_REQUEST.TOP, { hotelID })}
										title={"返金"}
									/>
									<SideMenuItem
										href={generatePath(PATHS.HOTEL.MANAGEMENT.ACTIVITIES.OPTIONS, { hotelID })}
										title="オプション管理"
									/>
								</SideMenuItemWrapper>
							)}

							{isHotelPolicyRegistered && (
								<SideMenuItemWrapper
									title={"在庫管理"}
									childrenPaths={[
										generatePath(PATHS.HOTEL.ITEMS.PLAN.STOCK, { hotelID }),
										generatePath(PATHS.HOTEL.ITEMS.ROOM.STOCK, { hotelID }),
										generatePath(PATHS.HOTEL.ITEMS.OPTION.STOCK, { hotelID }),
									]}
									icon={<Icon.InventoryIcon />}
								>
									<SideMenuItem
										href={generatePath(PATHS.HOTEL.ITEMS.PLAN.STOCK, { hotelID })}
										title={"プラン"}
									/>
									<SideMenuItem
										href={generatePath(PATHS.HOTEL.ITEMS.ROOM.STOCK, { hotelID })}
										title={"部屋"}
									/>
									<SideMenuItem
										href={generatePath(PATHS.HOTEL.ITEMS.OPTION.STOCK, { hotelID })}
										title={"オプション"}
									/>
								</SideMenuItemWrapper>
							)}

							{isHotelPolicyRegistered && (
								<SideMenuItemWrapper
									title={"料金管理"}
									childrenPaths={[
										generatePath(PATHS.HOTEL.ITEMS.PLAN.RATE_MATRIX, { hotelID }),
										generatePath(PATHS.HOTEL.ITEMS.ROOM.PRICE_RANK, { hotelID }),
									]}
									icon={<Icon.PriceCheckIcon />}
								>
									<SideMenuItem
										href={generatePath(PATHS.HOTEL.ITEMS.PLAN.RATE_MATRIX, { hotelID })}
										title={"料金一覧"}
										titleRightChildren={<Label text="ベータ" variant="black" size="xs" />}
									/>
									<SideMenuItem
										href={generatePath(PATHS.HOTEL.ITEMS.ROOM.PRICE_RANK, { hotelID })}
										title={"部屋料金"}
									/>
								</SideMenuItemWrapper>
							)}

							{isHotelPolicyRegistered && (
								<SideMenuItemWrapper
									title={"商品管理"}
									useAmbiguousMatching
									childrenPaths={[
										generatePath(PATHS.HOTEL.ITEMS.PLAN.TOP, { hotelID }),
										generatePath(PATHS.HOTEL.ITEMS.ROOM.TOP, { hotelID }),
										generatePath(PATHS.HOTEL.ITEMS.OPTION.TOP, { hotelID }),
										generatePath(PATHS.HOTEL.ITEMS.DISCOUNT.TOP, { hotelID }),
									]}
									excludePaths={[
										generatePath(PATHS.HOTEL.ITEMS.PLAN.STOCK, { hotelID }),
										generatePath(PATHS.HOTEL.ITEMS.ROOM.STOCK, { hotelID }),
										generatePath(PATHS.HOTEL.ITEMS.OPTION.STOCK, { hotelID }),
										generatePath(PATHS.HOTEL.ITEMS.ROOM.PRICE_RANK, { hotelID }),
										generatePath(PATHS.HOTEL.ITEMS.PLAN.RATE_MATRIX, { hotelID }),
									]}
									icon={<Icon.GoodsIcon />}
								>
									<SideMenuItem
										href={generatePath(PATHS.HOTEL.ITEMS.PLAN.TOP, { hotelID })}
										ambiguousPath={[generatePath(PATHS.HOTEL.ITEMS.PLAN.TOP, { hotelID })]}
										excludePath={[
											generatePath(PATHS.HOTEL.ITEMS.PLAN.STOCK, { hotelID }),
											generatePath(PATHS.HOTEL.ITEMS.PLAN.RATE_MATRIX, { hotelID }),
										]}
										title={"プラン"}
									/>
									<SideMenuItem
										href={generatePath(PATHS.HOTEL.ITEMS.ROOM.TOP, { hotelID })}
										ambiguousPath={[generatePath(PATHS.HOTEL.ITEMS.ROOM.TOP, { hotelID })]}
										excludePath={[
											generatePath(PATHS.HOTEL.ITEMS.ROOM.STOCK, { hotelID }),
											generatePath(PATHS.HOTEL.ITEMS.ROOM.PRICE_RANK, { hotelID }),
										]}
										title={"部屋"}
									/>
									<SideMenuItem
										href={generatePath(PATHS.HOTEL.ITEMS.OPTION.TOP, { hotelID })}
										ambiguousPath={[generatePath(PATHS.HOTEL.ITEMS.OPTION.TOP, { hotelID })]}
										excludePath={generatePath(PATHS.HOTEL.ITEMS.OPTION.STOCK, { hotelID })}
										title={"オプション"}
									/>
									<SideMenuItem
										href={generatePath(PATHS.HOTEL.ITEMS.DISCOUNT.TOP, { hotelID })}
										ambiguousPath={[generatePath(PATHS.HOTEL.ITEMS.DISCOUNT.TOP, { hotelID })]}
										title="ディスカウント"
									/>
								</SideMenuItemWrapper>
							)}

							<SideMenuItemWrapper
								icon={<Icon.AccommodationIcon />}
								title={"施設設定"}
								useAmbiguousMatching
								childrenPaths={[
									generatePath(PATHS.HOTEL.HOTEL_SETTINGS.EDIT.DEFAULT, { hotelID }),
									generatePath(PATHS.HOTEL.HOTEL_SETTINGS.HOTEL_POLICY.TOP, { hotelID }),
									generatePath(PATHS.HOTEL.HOTEL_SETTINGS.EMAIL, { hotelID }),
									generatePath(PATHS.HOTEL.HOTEL_SETTINGS.MAIL_SETTINGS.TOP, { hotelID }),
								]}
							>
								<SideMenuItem
									href={generatePath(PATHS.HOTEL.HOTEL_SETTINGS.EDIT.DEFAULT, { hotelID })}
									ambiguousPath={[generatePath(PATHS.HOTEL.HOTEL_SETTINGS.EDIT.DEFAULT, { hotelID })]}
									title={"施設情報"}
								/>
								{isHotelRegistered && (
									<SideMenuItem
										href={generatePath(PATHS.HOTEL.HOTEL_SETTINGS.HOTEL_POLICY.TOP, { hotelID })}
										ambiguousPath={[
											generatePath(PATHS.HOTEL.HOTEL_SETTINGS.HOTEL_POLICY.TOP, { hotelID }),
										]}
										title={"ホテルポリシー"}
									/>
								)}
								{isHotelRegistered && (
									<SideMenuItem
										href={generatePath(PATHS.HOTEL.HOTEL_SETTINGS.MAIL_SETTINGS.TOP, { hotelID })}
										ambiguousPath={[
											generatePath(PATHS.HOTEL.HOTEL_SETTINGS.MAIL_SETTINGS.TOP, { hotelID }),
										]}
										title={"基本メール設定"}
									/>
								)}
								{isHotelPolicyRegistered && (
									<SideMenuItem
										href={generatePath(PATHS.HOTEL.HOTEL_SETTINGS.EMAIL, { hotelID })}
										title="通知設定"
									/>
								)}
							</SideMenuItemWrapper>

							<SideMenuItemWrapper
								title={"設定"}
								useAmbiguousMatching
								childrenPaths={[
									generatePath(PATHS.SETTINGS.PAYMENT_CREDIT, { hotelID }),
									generatePath(PATHS.SETTINGS.PAYMENT_DEPOSIT_ACCOUNT, { hotelID }),
									generatePath(PATHS.SETTINGS.BILLING, { hotelID }),
									generatePath(PATHS.SETTINGS.SECURITY, { hotelID }),
								]}
								icon={<Icon.SettingsIcon />}
							>
								<SideMenuItem
									href={generatePath(PATHS.SETTINGS.PAYMENT_CREDIT, { hotelID })}
									title="お支払い情報"
								/>
								<SideMenuItem
									href={generatePath(PATHS.SETTINGS.PAYMENT_DEPOSIT_ACCOUNT, { hotelID })}
									title="入金先情報"
								/>
								<SideMenuItem
									href={generatePath(PATHS.SETTINGS.BILLING, { hotelID })}
									title={"料金プラン"}
								/>
								<SideMenuItem
									href={generatePath(PATHS.SETTINGS.SECURITY, { hotelID })}
									title="セキュリティ"
								/>
							</SideMenuItemWrapper>

							{isHotelPolicyRegistered && (
								<SideMenuItemWrapper
									icon={<Icon.CloudSyncIcon />}
									title={"外部接続設定"}
									useAmbiguousMatching
									childrenPaths={[
										generatePath(PATHS.SETTINGS.SITE_CONTROLLER, { hotelID }),
										generatePath(
											PATHS.SETTINGS.EXTERNAL_CONNECTION.GOOGLE_FREE_BOOKING_LINKS.DEFAULT,
											{
												hotelID,
											},
										),
										generatePath(PATHS.SETTINGS.EXTERNAL_CONNECTION.SITE_EMBED, { hotelID }),
									]}
								>
									<SideMenuItem
										href={generatePath(PATHS.SETTINGS.SITE_CONTROLLER, { hotelID })}
										title={"サイトコントローラー"}
									/>
									<SideMenuItem
										href={generatePath(
											PATHS.SETTINGS.EXTERNAL_CONNECTION.GOOGLE_FREE_BOOKING_LINKS.DEFAULT,
											{
												hotelID,
											},
										)}
										title={"Google 無料予約リンク"}
									/>
									<SideMenuItem
										href={generatePath(PATHS.SETTINGS.EXTERNAL_CONNECTION.SITE_EMBED, { hotelID })}
										title={"サイト埋め込み"}
									/>
								</SideMenuItemWrapper>
							)}

							<div className="my-4 border-b border-zinc-200" />

							<SideMenuItem
								href={externalLink.faqSiteUrl(hotelID)}
								title="困ったときは"
								targetBlank
								icon={<Icon.SupportIcon className="h-[22px] w-[22px]" />}
							/>

							<SideMenuItem
								href="https://typebot.co/chillnn-reservation-test-y04lb9k"
								title="予約できないときは"
								targetBlank
								icon={<Icon.DiagnosticIcon className="h-[22px] w-[22px]" />}
							/>
						</div>

						<SideMenuItemOther hotelID={hotelID} />
					</div>
				</div>
			</aside>
		);
	},
);

SideMenuPCPresenter.displayName = "SideMenuPCPresenter";
